'use client';

import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Text from '@tiptap/extension-text';
import { Fragment, type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { useToast } from '@/app/components/ui/use-toast';
import type { EmotionType } from '@/app/types';
import { cn } from '@/app/utils';
import { getTtsTextLength, PAUSE_REGEX, PAUSE_SINGLE_REGEX, TTS_CARET_ANCHOR, TTS_LIMITS } from '../constant';
import type { ActiveEmotionTag, ActivePauseTag } from '../types';
import {
  createEditorContentFromValue,
  createEmotionNodes,
  extractInlineSelectionContent,
  findTtsEmotionSyntax,
  getFirstEmptyEmotionTagRange,
  getPauseTagLength,
  getTextLength,
  hasEmotionEligibleSelectionContent,
  hasMeaningfulContent,
  insertTextWithSyntax,
  removeFirstEmptyEmotionTag,
  serializeEditorContent,
  serializeSelectionContent,
  stripCaretAnchorsFromFragment,
  type TtsTranslate,
  truncateTtsTextByLength,
} from '../utils';
import { EmotionPopover } from './EmotionPopover';
import { EmotionSelectorPopover } from './EmotionSelectorPopover';
import { EmotionTag } from './EmotionTag';
import { PausePopover } from './PausePopover';
import { PauseTag } from './PauseTag';

interface TTSTextEditorProps {
  value: string;
  onChange: (text: string) => void;
}

type SyntaxReplacement = {
  from: number;
  to: number;
  content: Fragment | ProseMirrorNode | ProseMirrorNode[];
};

const SingleParagraphDocument = Document.extend({
  content: 'inline*',
});

export default function TTSTextEditor({ value, onChange }: TTSTextEditorProps) {
  const t = useTranslations();
  const { toast } = useToast();

  const [hasSelection, setHasSelection] = useState(false);
  const [selectionHasEmotion, setSelectionHasEmotion] = useState(false);
  const [activePauseTag, setActivePauseTag] = useState<ActivePauseTag | null>(null);
  const [activeEmotionTag, setActiveEmotionTag] = useState<ActiveEmotionTag | null>(null);
  const [showSelectionEmotionPopover, setShowSelectionEmotionPopover] = useState(false);
  const [selectionAnchorRect, setSelectionAnchorRect] = useState<DOMRect | null>(null);
  const [charCount, setCharCount] = useState(0);

  const suppressSelectionUpdate = useRef(false);
  const savedSelectionRef = useRef<{ from: number; to: number } | null>(null);
  const activeEmotionTagElementRef = useRef<HTMLElement | null>(null);

  const handleEditorUpdate = useCallback(
    ({ editor }: { editor: Editor }) => {
      const emptyEmotionTagRange = getFirstEmptyEmotionTagRange(editor.state.doc);

      if (removeFirstEmptyEmotionTag(editor, emptyEmotionTagRange)) {
        setActiveEmotionTag(null);
        return;
      }

      const text = serializeEditorContent(editor);
      const nextTextLength = getTextLength(editor);

      if (nextTextLength > TTS_LIMITS.MAX_TEXT_LENGTH) {
        const truncatedText = truncateTtsTextByLength(text, TTS_LIMITS.MAX_TEXT_LENGTH);

        editor.commands.setContent(createEditorContentFromValue(truncatedText, t), false);
        setCharCount(getTtsTextLength(truncatedText));
        onChange(truncatedText);
        toast({
          type: 'warning',
          message: t('tools.textToSpeech.validation.textTooLong'),
        });
        return;
      }

      setCharCount(nextTextLength);
      onChange(text);
    },
    [onChange],
  );

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [SingleParagraphDocument, Text, History, PauseTag, EmotionTag],
    content: createEditorContentFromValue(value, t),
    editorProps: {
      attributes: {
        class: 'max-h-[260px] h-full overflow-y-auto whitespace-normal outline-none leading-[30px]',
      },
      handleDOMEvents: {
        mousedown: (_view, event) => {
          const target = event.target as HTMLElement;
          const pauseTagEl = target.closest('[data-pause-tag]');
          // Suppress selection update when clicking on tags or delete buttons
          if (pauseTagEl || target.closest('[data-emotion-label]') || target.closest('[data-emotion-delete]')) {
            suppressSelectionUpdate.current = true;
          }
          return false;
        },
        focus: (_view, event) => {
          const target = event.target as HTMLElement;
          if (target.closest('[data-emotion-content]')) {
            return false;
          }

          if (moveCursorAfterTrailingEmotionTag(editor?.view ?? null)) {
            return true;
          }

          return false;
        },
        click: (view, event) => {
          const target = event.target as HTMLElement;
          const pauseTagEl = target.closest('[data-pause-tag]') as HTMLElement | null;

          // Handle emotion delete button click
          if (target.closest('[data-emotion-delete]')) {
            event.preventDefault();
            event.stopPropagation();
            const tagEl = target.closest('[data-emotion-tag]') as HTMLElement;
            if (tagEl) {
              const emotionId = tagEl.getAttribute('data-emotion-id') || '';
              const emotionTagRange = getEmotionTagRangeFromDom(view, tagEl);
              if (emotionTagRange) {
                deleteEmotionAtPos(view, emotionTagRange.from, emotionId);
                setActiveEmotionTag(null);
              }
            }
            return true;
          }

          // Handle pause tag click
          if (pauseTagEl) {
            const duration = Number.parseFloat(pauseTagEl.getAttribute('data-duration') || '0.5');
            const disabled = pauseTagEl.getAttribute('data-disabled') === 'true';
            if (disabled) return true;
            const pos = view.posAtDOM(pauseTagEl, 0);

            if (activePauseTag && activePauseTag.pos === pos) {
              setActivePauseTag(null);
              return true;
            }
            setActivePauseTag({ pos, duration, element: pauseTagEl, disabled });
            setActiveEmotionTag(null);
            setShowSelectionEmotionPopover(false);
            return true;
          }

          if (target.closest('[data-emotion-content]')) {
            setActivePauseTag(null);
            setActiveEmotionTag(null);
            setShowSelectionEmotionPopover(false);
            return false;
          }

          // Handle emotion label click only
          const emotionLabelEl = target.closest('[data-emotion-label]') as HTMLElement;
          if (emotionLabelEl) {
            const emotionTagEl = emotionLabelEl.closest('[data-emotion-tag]') as HTMLElement | null;
            if (!emotionTagEl) return false;

            const emotionId = emotionTagEl.getAttribute('data-emotion-id') || '';
            const emotionTagRange = getEmotionTagRangeFromDom(view, emotionTagEl);
            if (!emotionTagRange) return false;
            const pos = emotionTagRange.from;

            if (activeEmotionTag && activeEmotionTag.pos === pos) {
              setActiveEmotionTag(null);
              return true;
            }

            setActiveEmotionTag({
              pos,
              emotionId,
              tagBackgroundColor: emotionLabelEl.style.backgroundColor,
              tagTextColor: emotionLabelEl.style.color,
              element: emotionLabelEl,
            });
            setActivePauseTag(null);
            setShowSelectionEmotionPopover(false);
            return true;
          }

          // Close popovers on other clicks
          setActivePauseTag(null);
          setActiveEmotionTag(null);
          setShowSelectionEmotionPopover(false);
          return false;
        },
        copy: (_view, event) => {
          if (!editor || !event.clipboardData || editor.state.selection.empty) {
            return false;
          }

          event.preventDefault();
          event.clipboardData.setData('text/plain', serializeSelectionContent(editor));
          return true;
        },
      },
      handlePaste(_view, event) {
        const text = event.clipboardData?.getData('text/plain');
        if (!text || !editor) return false;

        event.preventDefault();

        const selectedTextLength = getTtsTextLength(serializeSelectionContent(editor));
        const availableLength = TTS_LIMITS.MAX_TEXT_LENGTH - (getTextLength(editor) - selectedTextLength);
        const shouldTruncate = getTtsTextLength(text) > availableLength;
        const nextText = shouldTruncate ? truncateTtsTextByLength(text, availableLength) : text;

        if (!nextText) {
          toast({
            type: 'warning',
            message: t('tools.textToSpeech.validation.textTooLong'),
          });
          return true;
        }

        const pausePattern = PAUSE_REGEX;
        pausePattern.lastIndex = 0;
        if (pausePattern.test(nextText) || findTtsEmotionSyntax(nextText)) {
          insertTextWithSyntax(editor, nextText, t);
        } else {
          editor.commands.insertContent(nextText);
        }

        if (shouldTruncate) {
          toast({
            type: 'warning',
            message: t('tools.textToSpeech.validation.textTooLong'),
          });
        }

        return true;
      },
      handleKeyDown(_view, event) {
        if (!editor) return false;

        if (event.key === 'Enter') {
          event.preventDefault();
          return true;
        }

        if (event.key === 'Backspace') {
          const movedIntoTrailingEmotionTag = moveCursorIntoTrailingEmotionTagBeforeCursor(editor);
          const deletedCharacterInsideEmotionTag = movedIntoTrailingEmotionTag
            ? false
            : deleteCharacterInsideEmotionTagBeforeCursor(editor);
          const handledEmotionContentStartBackspace =
            movedIntoTrailingEmotionTag || deletedCharacterInsideEmotionTag
              ? false
              : handleEmotionContentStartBackspace(editor);
          const deletedPauseTagBeforeCursor =
            movedIntoTrailingEmotionTag || deletedCharacterInsideEmotionTag || handledEmotionContentStartBackspace
              ? false
              : tryDeletePauseTagBeforeCursor(editor);

          if (
            movedIntoTrailingEmotionTag ||
            deletedCharacterInsideEmotionTag ||
            handledEmotionContentStartBackspace ||
            deletedPauseTagBeforeCursor
          ) {
            event.preventDefault();
            return true;
          }
        }

        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          const currentLen = getTextLength(editor);
          const selectedTextLength = getTtsTextLength(serializeSelectionContent(editor));
          const nextLength = currentLen - selectedTextLength + getTtsTextLength(event.key);
          if (nextLength > TTS_LIMITS.MAX_TEXT_LENGTH) {
            toast({
              type: 'warning',
              message: t('tools.textToSpeech.validation.textTooLong'),
            });
            event.preventDefault();
            return true;
          }
        }
        return false;
      },
    },
    onUpdate: handleEditorUpdate,
    onSelectionUpdate: ({ editor: ed }) => {
      // Don't update selection state when clicking on tags
      if (suppressSelectionUpdate.current) {
        suppressSelectionUpdate.current = false;
        return;
      }

      const { from, to } = ed.state.selection;
      const selected = from !== to;
      let hasEmotionMark = false;

      if (selected) {
        hasEmotionMark = isSelectionInsideEmotionTag(ed);
        if (!hasEmotionMark) {
          ed.state.doc.nodesBetween(from, to, (node) => {
            if (node.type.name === 'emotionTag') {
              hasEmotionMark = true;
            }
            return !hasEmotionMark;
          });
        }
      }
      setHasSelection(selected && !hasEmotionMark && hasEmotionEligibleSelectionContent(ed));
      setSelectionHasEmotion(hasEmotionMark);
    },
  });

  useEffect(() => {
    if (!editor) return;

    const nextValue = value || '';
    if (serializeEditorContent(editor) === nextValue) {
      setCharCount(getTextLength(editor));
      return;
    }

    editor.commands.setContent(createEditorContentFromValue(nextValue, t), false);
    setCharCount(getTextLength(editor));
    setHasSelection(false);
    setSelectionHasEmotion(false);
    setActivePauseTag(null);
    setActiveEmotionTag(null);
    setShowSelectionEmotionPopover(false);
    setSelectionAnchorRect(null);
  }, [editor, value, t]);

  // Auto-detect pause and emotion patterns after text input
  useEffect(() => {
    if (!editor) return;

    const checkForSyntaxPatterns = () => {
      const maxReplacementCount = 50;

      for (let replacementCount = 0; replacementCount < maxReplacementCount; replacementCount += 1) {
        const replacement = findNextSyntaxReplacement(editor, t);
        if (!replacement) {
          return;
        }

        editor.view.dispatch(editor.state.tr.replaceWith(replacement.from, replacement.to, replacement.content));
      }
    };

    const timeout = setTimeout(checkForSyntaxPatterns, 300);
    return () => clearTimeout(timeout);
  }, [editor, charCount, t]);

  const handleInsertPause = useCallback(() => {
    if (!editor) return;

    const currentLen = getTextLength(editor);
    const nextPauseLength = getPauseTagLength(0.5);
    if (currentLen + nextPauseLength > TTS_LIMITS.MAX_TEXT_LENGTH) {
      toast({
        type: 'warning',
        message: t('tools.textToSpeech.validation.textTooLong'),
      });
      return;
    }

    editor.chain().focus().insertPauseTag(0.5).run();
  }, [editor, t, toast]);

  const handleEmotionButtonClick = useCallback(() => {
    if (!editor || !hasSelection || selectionHasEmotion) return;

    const { from, to } = editor.state.selection;
    savedSelectionRef.current = { from, to };

    const domSelection = window.getSelection();
    if (domSelection && domSelection.rangeCount > 0) {
      const range = domSelection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      setSelectionAnchorRect(rect);
      setShowSelectionEmotionPopover(true);
      setActivePauseTag(null);
      setActiveEmotionTag(null);
    }
  }, [editor, hasSelection, selectionHasEmotion]);

  const handleEmotionSelect = useCallback(
    (emotion: EmotionType) => {
      if (!editor || !savedSelectionRef.current) return;

      applyEmotionToSelection(editor, savedSelectionRef.current, {
        emotionId: emotion.id,
        emotionName: t(emotion.labelKey),
        tagBackgroundColor: emotion.tagBackgroundColor,
        tagTextColor: emotion.tagTextColor,
        emotionTextBackgroundColor: emotion.emotionTextBackgroundColor,
      });

      setHasSelection(false);
      setSelectionHasEmotion(false);
      setShowSelectionEmotionPopover(false);
      setSelectionAnchorRect(null);
      savedSelectionRef.current = null;
    },
    [editor, t],
  );

  const handlePauseTagChange = useCallback(
    (duration: number) => {
      if (!editor || !activePauseTag) return;

      const currentLen = getTextLength(editor);
      const nextTotalLength = currentLen - getPauseTagLength(activePauseTag.duration) + getPauseTagLength(duration);

      if (nextTotalLength > TTS_LIMITS.MAX_TEXT_LENGTH) {
        toast({
          type: 'warning',
          message: t('tools.textToSpeech.validation.textTooLong'),
        });
        return;
      }

      const { tr } = editor.state;
      tr.setNodeMarkup(activePauseTag.pos, undefined, {
        duration,
        disabled: false,
      });
      editor.view.dispatch(tr);
      setActivePauseTag(null);
    },
    [editor, activePauseTag, t, toast],
  );

  const handleEmotionTagChange = useCallback(
    (emotion: EmotionType) => {
      if (!editor || !activeEmotionTag) return;

      const { tr } = editor.state;
      tr.setNodeMarkup(activeEmotionTag.pos, undefined, {
        emotionId: emotion.id,
        emotionName: t(emotion.labelKey),
        tagBackgroundColor: emotion.tagBackgroundColor,
        tagTextColor: emotion.tagTextColor,
        emotionTextBackgroundColor: emotion.emotionTextBackgroundColor,
      });

      editor.view.dispatch(tr);
      setActiveEmotionTag(null);
    },
    [editor, activeEmotionTag, t],
  );

  const emotionButtonEnabled = hasSelection && !selectionHasEmotion;

  useEffect(() => {
    if (activeEmotionTagElementRef.current) {
      activeEmotionTagElementRef.current.removeAttribute('data-emotion-active');
      activeEmotionTagElementRef.current = null;
    }

    if (!activeEmotionTag?.element) {
      return;
    }

    const emotionTagElement = activeEmotionTag.element.closest('[data-emotion-tag]');
    if (emotionTagElement instanceof HTMLElement) {
      emotionTagElement.setAttribute('data-emotion-active', 'true');
      activeEmotionTagElementRef.current = emotionTagElement;
    }
  }, [activeEmotionTag]);

  return (
    <div className="relative flex min-h-[240px] w-full flex-col border-system-white08 border-b pb-3">
      {charCount === 0 && (
        <div
          className="pointer-events-none absolute select-none font-normal text-sm text-system-white36 leading-[30px]"
          style={{ top: 12, left: 12, right: 12 }}
        >
          {t('tools.textToSpeech.textPlaceholder')}
        </div>
      )}

      <EditorContent
        editor={editor}
        className="min-h-0 flex-1 rounded-12 border border-system-white08 bg-black/[36%] p-3"
      />

      {/* Pause tag popover */}
      {activePauseTag && !activePauseTag.disabled && (
        <PausePopover
          pauseTag={activePauseTag}
          onChange={handlePauseTagChange}
          onClose={() => setActivePauseTag(null)}
        />
      )}

      {/* Emotion tag popover (for editing existing tags) */}
      {activeEmotionTag && (
        <EmotionPopover
          emotionTag={activeEmotionTag}
          onChange={handleEmotionTagChange}
          onClose={() => setActiveEmotionTag(null)}
        />
      )}

      {/* Selection emotion popover (for adding new emotion to selected text) */}
      {showSelectionEmotionPopover && selectionAnchorRect && (
        <EmotionSelectorPopover
          anchorRect={selectionAnchorRect}
          onSelect={handleEmotionSelect}
          onClose={() => setShowSelectionEmotionPopover(false)}
        />
      )}

      {/* Bottom toolbar */}
      <div className="mt-3 flex items-center justify-between pt-3">
        <div className="flex items-center gap-3">
          {/* Emotion button */}
          <CustomTooltip content={emotionButtonEnabled ? '' : t('tools.textToSpeech.emotionButtonTooltip')}>
            <button
              type="button"
              className={cn(
                'flex h-10 items-center justify-center gap-2 rounded-xl border px-[17px] py-[9px] text-sm leading-[27px] transition-colors',
                emotionButtonEnabled
                  ? 'cursor-pointer border-system-white12 bg-system-white08 text-system-white hover:bg-system-white12'
                  : 'cursor-not-allowed border-system-white08 bg-system-white04 text-system-white/30',
              )}
              disabled={!emotionButtonEnabled}
              onClick={handleEmotionButtonClick}
            >
              <BaseLinePlus className="size-5 shrink-0" />
              {t('tools.textToSpeech.emotionButton')}
            </button>
          </CustomTooltip>

          <div className="h-6 w-px shrink-0 bg-system-white12" />

          {/* Pause button */}
          <button
            type="button"
            className="flex h-10 cursor-pointer items-center justify-center gap-2 rounded-xl border border-system-white12 bg-system-white08 px-[17px] py-[9px] text-sm text-system-white leading-[27px] transition-colors hover:bg-system-white12"
            onClick={handleInsertPause}
          >
            <span className="w-5 shrink-0 text-center text-base leading-none">#</span>
            {t('tools.textToSpeech.pauseButton')}
          </button>
        </div>

        {/* Character counter */}
        <span className="text-sm text-system-white/40 leading-[27px]">
          {charCount}/{TTS_LIMITS.MAX_TEXT_LENGTH}
        </span>
      </div>
    </div>
  );
}

function deleteEmotionAtPos(view: Editor['view'], pos: number, _emotionId: string) {
  const { tr } = view.state;
  const emotionTagRange = getEmotionTagRangeAtPosition(view.state.doc, pos);
  if (!emotionTagRange) return;

  if (emotionTagRange.node.content.size) {
    tr.replaceWith(
      emotionTagRange.from,
      emotionTagRange.to,
      stripCaretAnchorsFromFragment(emotionTagRange.node.content, view.state.schema),
    );
  } else {
    tr.delete(emotionTagRange.from, emotionTagRange.to);
  }

  view.dispatch(tr);
}

function applyEmotionToSelection(
  editor: Editor,
  selection: { from: number; to: number },
  emotion: {
    emotionId: string;
    emotionName: string;
    tagBackgroundColor: string;
    tagTextColor: string;
    emotionTextBackgroundColor: string;
  },
) {
  const { from, to } = selection;
  if (from >= to) return;

  const { state, view } = editor;
  const selectedContent = extractInlineSelectionContent(state.doc.slice(from, to).content, editor.schema);
  const tagNode = editor.schema.nodes.emotionTag.create(
    {
      ...emotion,
    },
    selectedContent,
  );
  const caretAnchorNode = editor.schema.text(TTS_CARET_ANCHOR);
  const tr = state.tr.replaceWith(from, to, Fragment.fromArray([tagNode, caretAnchorNode]));
  const selectionPos = from + tagNode.nodeSize + caretAnchorNode.nodeSize;

  tr.setSelection(TextSelection.create(tr.doc, selectionPos));
  tr.setStoredMarks([]);

  view.dispatch(tr);
  view.focus();
}

function findNextSyntaxReplacement(editor: Editor, t: TtsTranslate): SyntaxReplacement | null {
  const { doc, schema } = editor.state;
  let replacement: SyntaxReplacement | null = null;

  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) {
      return true;
    }

    const text = node.text;
    const emotionMatch = findTtsEmotionSyntax(text);
    if (emotionMatch && !isPositionInsideEmotionTag(editor, pos)) {
      const [rawMatch, emotionId, emotionText] = emotionMatch;
      const from = pos + (emotionMatch.index ?? 0);
      replacement = {
        from,
        to: from + rawMatch.length,
        content: createEmotionNodes(editor, emotionId, emotionText, t),
      };
      return false;
    }

    const pauseMatch = text.match(PAUSE_SINGLE_REGEX);
    if (!pauseMatch) {
      return true;
    }

    const duration = Number.parseFloat(pauseMatch[1]);
    const from = pos + (pauseMatch.index ?? 0);
    const isOutOfRange = duration < TTS_LIMITS.MIN_PAUSE || duration > TTS_LIMITS.MAX_PAUSE;
    replacement = {
      from,
      to: from + pauseMatch[0].length,
      content: schema.nodes.pauseTag.create({
        duration,
        disabled: isOutOfRange,
      }),
    };
    return false;
  });

  return replacement;
}

function isPositionInsideEmotionTag(editor: Editor, pos: number) {
  const $pos = editor.state.doc.resolve(pos);
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    if ($pos.node(depth).type.name === 'emotionTag') {
      return true;
    }
  }
  return false;
}

function isSelectionInsideEmotionTag(editor: Editor) {
  const { from, to } = editor.state.selection;
  return isPositionInsideEmotionTag(editor, from) || isPositionInsideEmotionTag(editor, to);
}

function handleEmotionContentStartBackspace(editor: Editor) {
  const { selection, doc, tr } = editor.state;
  if (!selection.empty) {
    return false;
  }

  const emotionTagContext = getEmotionTagContextAtResolvedPos(selection.$from);
  if (!emotionTagContext) {
    const adjacentEmptyEmotionTag = getAdjacentEmptyEmotionTagAtCursor(doc, selection.from);
    if (!adjacentEmptyEmotionTag) {
      return false;
    }

    tr.delete(adjacentEmptyEmotionTag.from, adjacentEmptyEmotionTag.to);
    editor.view.dispatch(tr);
    return true;
  }

  if (!hasMeaningfulContent(emotionTagContext.node.content)) {
    tr.delete(emotionTagContext.from, emotionTagContext.to);
    editor.view.dispatch(tr);
    return true;
  }

  const isAtEmotionContentStart = selection.$from.pos === selection.$from.start(emotionTagContext.depth);
  return isAtEmotionContentStart;
}

function deleteCharacterInsideEmotionTagBeforeCursor(editor: Editor) {
  const { selection, doc, tr } = editor.state;
  if (!selection.empty || selection.from === 0) {
    return false;
  }

  const emotionTagContext = getEmotionTagContextAtResolvedPos(selection.$from);
  if (!emotionTagContext) {
    return false;
  }

  const deleteFrom = selection.from - 1;
  if (deleteFrom < selection.$from.start(emotionTagContext.depth)) {
    return false;
  }

  const charBeforeCursor = doc.textBetween(deleteFrom, selection.from, '', '');
  if (!charBeforeCursor || charBeforeCursor === TTS_CARET_ANCHOR) {
    return false;
  }

  tr.delete(deleteFrom, selection.from);
  editor.view.dispatch(tr);
  return true;
}

function moveCursorIntoTrailingEmotionTagBeforeCursor(editor: Editor) {
  const { selection, doc, tr } = editor.state;
  if (!selection.empty || selection.from === 0) {
    return false;
  }

  const cursorPos = selection.from;
  if (doc.textBetween(cursorPos - 1, cursorPos, '', '') !== TTS_CARET_ANCHOR) {
    return false;
  }

  const anchorPos = cursorPos - 1;
  const $anchorPos = doc.resolve(anchorPos);
  const nodeBeforeAnchor = $anchorPos.nodeBefore;
  if (nodeBeforeAnchor?.type.name !== 'emotionTag') {
    return false;
  }

  const emotionTagPos = anchorPos - nodeBeforeAnchor.nodeSize;
  const emotionTagRange = getEmotionTagRangeAtPosition(doc, emotionTagPos);
  if (!emotionTagRange || !hasOnlyCaretAnchorsAfter(doc, emotionTagRange.to)) {
    return false;
  }

  const contentEndPos = emotionTagRange.to - 1;
  tr.setSelection(TextSelection.create(doc, Math.max(emotionTagRange.from + 1, contentEndPos)));
  editor.view.dispatch(tr);
  return true;
}

function tryDeletePauseTagBeforeCursor(editor: Editor) {
  const { selection, doc, tr } = editor.state;
  if (!selection.empty || selection.from === 0) {
    return false;
  }

  const cursorPos = selection.from;
  if (doc.textBetween(cursorPos - 1, cursorPos, '', '') !== TTS_CARET_ANCHOR) {
    return false;
  }

  let pauseTagPos = cursorPos - 2;
  let pauseTagNode = pauseTagPos >= 0 ? doc.nodeAt(pauseTagPos) : null;

  if (pauseTagNode?.type.name !== 'pauseTag') {
    const $anchorPos = doc.resolve(cursorPos - 1);
    const nodeBeforeAnchor = $anchorPos.nodeBefore;
    if (nodeBeforeAnchor?.type.name !== 'pauseTag') {
      return false;
    }
    pauseTagPos = cursorPos - 1 - nodeBeforeAnchor.nodeSize;
    pauseTagNode = nodeBeforeAnchor;
  }

  if (pauseTagNode?.type.name !== 'pauseTag') {
    return false;
  }

  tr.delete(pauseTagPos, cursorPos);
  editor.view.dispatch(tr);
  return true;
}

function getEmotionTagRangeFromDom(view: Editor['view'], element: HTMLElement) {
  const rawPos = view.posAtDOM(element, 0);
  return getEmotionTagRangeAtPosition(view.state.doc, rawPos);
}

function getEmotionTagRangeAtPosition(doc: Editor['state']['doc'], pos: number) {
  const safePos = Math.min(pos, doc.content.size);
  const directNode = doc.nodeAt(safePos);

  if (directNode?.type.name === 'emotionTag') {
    return {
      from: safePos,
      to: safePos + directNode.nodeSize,
      node: directNode,
    };
  }

  const $pos = doc.resolve(safePos);
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    const node = $pos.node(depth);
    if (node.type.name === 'emotionTag') {
      return {
        from: $pos.before(depth),
        to: $pos.after(depth),
        node,
      };
    }
  }

  return null;
}

function getEmotionTagContextAtResolvedPos($pos: Editor['state']['selection']['$from']) {
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    const node = $pos.node(depth);
    if (node.type.name === 'emotionTag') {
      return {
        depth,
        from: $pos.before(depth),
        to: $pos.after(depth),
        node,
      };
    }
  }

  return null;
}

function getAdjacentEmptyEmotionTagAtCursor(doc: Editor['state']['doc'], cursorPos: number) {
  const $cursor = doc.resolve(cursorPos);
  const nodeBefore = $cursor.nodeBefore;

  if (nodeBefore?.type.name === 'emotionTag' && !hasMeaningfulContent(nodeBefore.content)) {
    return {
      from: cursorPos - nodeBefore.nodeSize,
      to: cursorPos,
    };
  }

  if (cursorPos === 0 || doc.textBetween(cursorPos - 1, cursorPos, '', '') !== TTS_CARET_ANCHOR) {
    return null;
  }

  const $anchorPos = doc.resolve(cursorPos - 1);
  const nodeBeforeAnchor = $anchorPos.nodeBefore;
  if (nodeBeforeAnchor?.type.name !== 'emotionTag' || hasMeaningfulContent(nodeBeforeAnchor.content)) {
    return null;
  }

  return {
    from: cursorPos - 1 - nodeBeforeAnchor.nodeSize,
    to: cursorPos,
  };
}

function moveCursorAfterTrailingEmotionTag(view: Editor['view'] | null) {
  if (!view) return false;

  const { selection, doc } = view.state;
  const emotionTagContext = getEmotionTagContextAtResolvedPos(selection.$from);
  if (!emotionTagContext) {
    return false;
  }

  if (!hasOnlyCaretAnchorsAfter(doc, emotionTagContext.to)) {
    return false;
  }

  const nextPos =
    doc.textBetween(emotionTagContext.to, emotionTagContext.to + 1, '', '') === TTS_CARET_ANCHOR
      ? emotionTagContext.to + 1
      : emotionTagContext.to;

  const tr = view.state.tr.setSelection(TextSelection.create(doc, nextPos));
  view.dispatch(tr);
  return true;
}

function hasOnlyCaretAnchorsAfter(doc: Editor['state']['doc'], pos: number) {
  for (let currentPos = pos; currentPos < doc.content.size; currentPos += 1) {
    const char = doc.textBetween(currentPos, currentPos + 1, '', '');
    if (!char) {
      continue;
    }
    if (char !== TTS_CARET_ANCHOR) {
      return false;
    }
  }

  return true;
}
